import type { components, operations } from '@molchanovs/api-contract';
import { z } from 'zod';

export const crmCustomerDocumentsViewId = 'crm.customerDocuments.v1' as const;
export const crmCustomerDocumentsPageSize = 10 as const;

export const crmCustomerDocumentTypeSchema = z.enum([
  'certification-card',
  'paper-exam',
  'paper-completion-form',
  'first-aid-certificate',
  'medical-questionnaire',
  'student-liability-release',
  'instructor-insurance-waiver',
  'unknown',
]);
export const crmCustomerDocumentStatusSchema = z.enum([
  'in-review',
  'approved',
  'not-valid',
  'draft',
  'cancelled',
  'unknown',
]);
export const crmCustomerDocumentFileStateSchema = z.enum([
  'available',
  'processing',
  'missing',
  'temporarily-unavailable',
  'no-file',
  'data-issue',
]);
export const crmCustomerDocumentSortSchema = z.enum([
  'updatedAt.desc',
  'updatedAt.asc',
  'expiresAt.asc',
  'expiresAt.desc',
  'type.asc',
  'type.desc',
]);

/** Public sort vocabulary for the canonical GET /api/v1/documents collection. */
export const crmDocumentEntitySortSchema = z.enum([
  'createdAt.desc',
  'updatedAt.desc',
  'updatedAt.asc',
  'expiryDate.asc',
  'expiryDate.desc',
  'type.asc',
  'type.desc',
]);

export const crmCustomerDocumentsResponseSchema = z
  .object({
    data: z
      .object({
        accountId: z.uuid().nullable(),
        allowedActions: z.tuple([z.literal('read')]),
        capabilities: z
          .object({
            download: z.enum(['allowed', 'denied']),
            preview: z.enum(['allowed', 'denied']),
            upload: z.enum(['allowed', 'denied', 'unavailable']),
          })
          .strict(),
        customerId: z.uuid(),
        filterOptions: z
          .object({
            documentTypes: z.array(crmCustomerDocumentTypeSchema),
            fileStates: z.array(crmCustomerDocumentFileStateSchema),
            registrations: z.array(
              z
                .object({
                  course: z
                    .object({
                      id: z.uuid(),
                      name: z.string().nullable(),
                      number: z.string().nullable(),
                      startDate: z.string().nullable(),
                    })
                    .strict()
                    .nullable(),
                  id: z.uuid(),
                  number: z.string().nullable(),
                })
                .strict()
            ),
            statuses: z.array(crmCustomerDocumentStatusSchema),
          })
          .strict(),
        rows: z.array(
          z
            .object({
              actionStates: z
                .object({
                  download: z.enum(['allowed', 'denied', 'unavailable']),
                  preview: z.enum(['allowed', 'denied', 'unavailable', 'unsupported']),
                })
                .strict(),
              allowedActions: z.tuple([z.literal('read')]),
              contentKind: z.enum(['data_native', 'file_backed']),
              createdAt: z.string().nullable(),
              displayName: z.string().nullable(),
              expiresAt: z.string().nullable(),
              fileState: crmCustomerDocumentFileStateSchema,
              format: z.enum(['pdf', 'image', 'doc', 'docx', 'unknown']),
              fileMetadata: z
                .object({
                  byteSize: z.number().int().positive(),
                  mimeType: z.string(),
                  originalFileName: z.string(),
                })
                .strict()
                .nullable(),
              id: z.uuid(),
              isExpired: z.boolean(),
              lifecycleState: z.enum(['synchronized', 'pending_sync', 'dispatching', 'failed']),
              operation: z
                .object({
                  actions: z
                    .object({ edit: z.boolean(), replaceFile: z.boolean(), retry: z.boolean() })
                    .strict(),
                  fields: z.lazy(() => crmCustomerDocumentFieldsSchema),
                  operationId: z.uuid(),
                  state: z.enum(['pending_sync', 'dispatching', 'failed']),
                  version: z.number().int().positive(),
                })
                .strict()
                .nullable(),
              provenance: z
                .object({
                  lastSyncedAt: z.string().nullable(),
                  sourceObject: z.enum(['user_doc', 'operation_journal']),
                  sourceSystem: z.enum(['bubble', 'move', 'unknown']),
                })
                .strict(),
              protectedReference: z
                .object({
                  documentId: z.uuid(),
                  documentType: z
                    .enum(['first-aid-certificate', 'instructor-insurance-waiver'])
                    .nullable(),
                  expiresAt: z.string().nullable(),
                  fileState: crmCustomerDocumentFileStateSchema,
                  invalidationState: z.enum(['active', 'invalid']),
                  readinessState: z.enum(['ready', 'not-ready']),
                  versionToken: z.string().regex(/^[a-f0-9]{64}$/),
                })
                .strict()
                .nullable(),
              registrationNumber: z.string().nullable(),
              status: crmCustomerDocumentStatusSchema,
              type: crmCustomerDocumentTypeSchema,
              uploadMode: z.enum(['file', 'digital-form', 'unknown']),
              updatedAt: z.string().nullable(),
            })
            .strict()
        ),
        state: z.enum(['empty', 'ready']),
        viewId: z.literal(crmCustomerDocumentsViewId),
      })
      .strict(),
    meta: z
      .object({
        filters: z
          .object({
            documentId: z.uuid().optional(),
            documentType: crmCustomerDocumentTypeSchema.optional(),
            fileState: crmCustomerDocumentFileStateSchema.optional(),
            registrationId: z.uuid().optional(),
            status: crmCustomerDocumentStatusSchema.optional(),
          })
          .strict(),
        page: z.number().int().positive(),
        pageSize: z.literal(crmCustomerDocumentsPageSize),
        q: z.string().nullable(),
        sort: crmCustomerDocumentSortSchema,
        totalItems: z.number().int().nonnegative(),
        totalPages: z.number().int().nonnegative(),
      })
      .strict(),
  })
  .strict();

export type CrmCustomerDocumentsResponse = z.infer<typeof crmCustomerDocumentsResponseSchema>;
export type CrmCustomerDocumentRow = CrmCustomerDocumentsResponse['data']['rows'][number];

/** Presentation availability only; protected Document routes authorize every file request. */
export function crmCustomerDocumentActionStates(
  fileState: CrmCustomerDocumentRow['fileState'],
  format: CrmCustomerDocumentRow['format']
): CrmCustomerDocumentRow['actionStates'] {
  const download = fileState === 'available' ? 'allowed' : 'unavailable';
  const preview =
    download !== 'allowed'
      ? 'unavailable'
      : format === 'image' || format === 'pdf'
        ? 'allowed'
        : 'unsupported';
  return { download, preview };
}
export type CrmCustomerDocumentType = z.infer<typeof crmCustomerDocumentTypeSchema>;
export type CrmCustomerDocumentStatus = z.infer<typeof crmCustomerDocumentStatusSchema>;
export type CrmCustomerDocumentFileState = z.infer<typeof crmCustomerDocumentFileStateSchema>;
export type CrmCustomerDocumentLifecycleState = CrmCustomerDocumentRow['lifecycleState'];
export type CrmCustomerDocumentSort = z.infer<typeof crmCustomerDocumentSortSchema>;
export type CrmDocumentEntitySort = z.infer<typeof crmDocumentEntitySortSchema>;
export type CrmCustomerDocumentFilters = CrmCustomerDocumentsResponse['meta']['filters'];

export const crmCustomerDocumentCommandTypeSchema = z.enum([
  'medical_questionnaire',
  'paper_completion_form',
  'paper_exam',
  'certification_card',
  'student_liability_release',
  'instructor_insurance',
  'first_aid_certificate',
]);

export const crmCustomerDocumentCommandStatusSchema = z.enum([
  'not_approved',
  'approved',
  'not_valid',
  'draft',
  'cancelled',
]);

export const crmCustomerDocumentFieldsSchema = z
  .object({
    certificationTypeId: z.uuid().nullable().optional(),
    certifyingAgency: z.string().nullable().optional(),
    examResultId: z.uuid().nullable().optional(),
    expiresAt: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    educationModuleId: z.uuid().nullable().optional(),
    paperExamDate: z.string().nullable().optional(),
    registrationId: z.uuid().nullable().optional(),
    submittingInstructorId: z.uuid().nullable().optional(),
    type: crmCustomerDocumentCommandTypeSchema,
  })
  .strict();

export const crmCustomerDocumentPatchSchema = crmCustomerDocumentFieldsSchema
  .omit({ type: true })
  .partial()
  .extend({ status: crmCustomerDocumentCommandStatusSchema.optional() })
  .strict();

export const crmCustomerDocumentProfileViewId = 'crm.customerDocumentProfile.v1' as const;

const crmCustomerDocumentProfileRelatedRecordSchema = z
  .object({ id: z.uuid(), name: z.string().nullable() })
  .strict()
  .nullable();

export const crmCustomerDocumentProfileResponseSchema = z
  .object({
    data: z
      .object({
        allowedActions: z.tuple([z.literal('read')]),
        audit: z
          .object({
            createdAt: z.string().nullable(),
            documentId: z.uuid(),
            lastSynchronizedAt: z.string().nullable(),
            source: z.enum(['bubble', 'postgres', 'unknown']),
            synchronizationStatus: z.enum([
              'pending_sync',
              'dispatching',
              'failed',
              'synchronized',
            ]),
            updatedAt: z.string().nullable(),
          })
          .strict(),
        capabilities: z
          .object({
            download: z.enum(['allowed', 'denied', 'unavailable']),
            preview: z.enum(['allowed', 'denied', 'unavailable', 'unsupported']),
          })
          .strict(),
        context: z
          .object({
            certifyingAgency: z.string().nullable(),
            certificationType: crmCustomerDocumentProfileRelatedRecordSchema,
            course: z
              .object({ id: z.uuid(), number: z.string().nullable(), title: z.string().nullable() })
              .strict()
              .nullable(),
            customer: z.object({ id: z.uuid(), name: z.string().nullable() }).strict(),
            examResult: crmCustomerDocumentProfileRelatedRecordSchema,
            educationModule: crmCustomerDocumentProfileRelatedRecordSchema,
            registration: z
              .object({
                id: z.uuid(),
                number: z.string().nullable(),
                submittedAt: z.string().nullable(),
              })
              .strict()
              .nullable(),
            submittingInstructor: crmCustomerDocumentProfileRelatedRecordSchema,
          })
          .strict(),
        customerId: z.uuid(),
        details: z
          .object({
            documentType: crmCustomerDocumentTypeSchema,
            expiresAt: z.iso.date().nullable(),
            notes: z.string().nullable(),
            paperExamDate: z.iso.date().nullable(),
            status: crmCustomerDocumentStatusSchema,
            uploadMode: z.enum(['file', 'digital-form', 'unknown']),
          })
          .strict(),
        guardianSignature: z
          .discriminatedUnion('state', [
            z.object({ state: z.literal('not-signed'), signedAt: z.null() }).strict(),
            z.object({ state: z.literal('signed'), signedAt: z.iso.datetime() }).strict(),
            z.object({ state: z.literal('unavailable'), signedAt: z.null() }).strict(),
          ])
          .nullable(),
        physicianApproval: z
          .discriminatedUnion('state', [
            z.object({ state: z.literal('not-recorded'), recordedAt: z.null() }).strict(),
            z.object({ state: z.literal('recorded'), recordedAt: z.iso.datetime() }).strict(),
            z.object({ state: z.literal('data-issue'), recordedAt: z.null() }).strict(),
          ])
          .nullable(),
        file: z
          .object({
            byteSize: z.number().int().positive().nullable(),
            downloadCapability: z.enum(['allowed', 'denied', 'unavailable']),
            downloadSupported: z.boolean(),
            format: z.enum(['pdf', 'image', 'doc', 'docx', 'unknown']),
            mimeType: z.string().nullable(),
            originalFileName: z.string().nullable(),
            previewCapability: z.enum(['allowed', 'denied', 'unavailable', 'unsupported']),
            previewSupported: z.boolean(),
            state: crmCustomerDocumentFileStateSchema,
          })
          .strict(),
        header: z
          .object({
            customer: z.object({ id: z.uuid(), name: z.string().nullable() }).strict(),
            documentId: z.uuid(),
            documentType: crmCustomerDocumentTypeSchema,
            expiresAt: z.iso.date().nullable(),
            registration: z
              .object({
                id: z.uuid(),
                number: z.string().nullable(),
                submittedAt: z.string().nullable(),
              })
              .strict()
              .nullable(),
            status: crmCustomerDocumentStatusSchema,
            synchronizationStatus: z.enum([
              'pending_sync',
              'dispatching',
              'failed',
              'synchronized',
            ]),
            updatedAt: z.string().nullable(),
          })
          .strict(),
        viewId: z.literal(crmCustomerDocumentProfileViewId),
      })
      .strict(),
  })
  .strict();

export const crmCustomerDocumentMutationResponseSchema = z
  .object({
    correlationId: z.string(),
    data: z
      .object({
        documentId: z.uuid(),
        synchronizationStatus: z.enum(['pending_sync', 'dispatching', 'failed']),
      })
      .strict(),
  })
  .strict();

const crmCustomerDocumentCreationOptionSchema = z
  .object({ id: z.uuid(), label: z.string() })
  .strict();
export const crmCustomerDocumentCreationOptionsResponseSchema = z
  .object({
    data: z
      .object({
        certificationTypes: z.array(crmCustomerDocumentCreationOptionSchema),
        certifyingAgencyInput: z.literal('free_text'),
        documentTypes: z.array(
          z
            .object({
              defaultStatus: crmCustomerDocumentCommandStatusSchema,
              type: crmCustomerDocumentCommandTypeSchema,
            })
            .strict()
        ),
        examResults: z.array(crmCustomerDocumentCreationOptionSchema),
        paperExamEms: z.array(crmCustomerDocumentCreationOptionSchema),
        registrations: z.array(
          crmCustomerDocumentCreationOptionSchema.extend({ courseId: z.uuid().nullable() }).strict()
        ),
        submittingInstructors: z.array(crmCustomerDocumentCreationOptionSchema),
      })
      .strict(),
  })
  .strict();

export type CrmCustomerDocumentCommandType = z.infer<typeof crmCustomerDocumentCommandTypeSchema>;
export type CrmCustomerDocumentCommandStatus = z.infer<
  typeof crmCustomerDocumentCommandStatusSchema
>;
export type CrmCustomerDocumentFields = z.infer<typeof crmCustomerDocumentFieldsSchema>;
export type CrmCustomerDocumentPatch = z.infer<typeof crmCustomerDocumentPatchSchema>;
export type CrmCustomerDocumentProfileResponse = z.infer<
  typeof crmCustomerDocumentProfileResponseSchema
>;
export type CrmCustomerDocumentMutationResponse = z.infer<
  typeof crmCustomerDocumentMutationResponseSchema
>;
export type CrmCustomerDocumentCreationOptionsResponse = z.infer<
  typeof crmCustomerDocumentCreationOptionsResponseSchema
>;

/** Generated canonical response for GET /api/v1/documents. */
export type CrmDocumentEntityListResponse =
  operations['getApiV1Documents']['responses'][200]['content']['application/json'];
/** Generated default GET response; the operation's 200 media entries are a negotiated union. */
export type CrmDocumentEntityDetailResponse = components['schemas']['CrmDocumentDetailResponse'];
export type CrmDocumentEntityRow = CrmDocumentEntityListResponse['data']['items'][number];
export type CrmDocumentEntityDetail = CrmDocumentEntityDetailResponse['data'];
/** Generated addressed alternative response for backend-owned Document action eligibility. */
export type CrmDocumentActionStateResponse =
  components['schemas']['CrmDocumentActionStateResponse'];

/** Canonical generated mutation contracts; legacy Bubble field aliases are not transport inputs. */
export type CrmDocumentCreateMetadata =
  operations['postApiV1Documents']['requestBody']['content']['multipart/form-data']['metadata'];
export type CrmDocumentCreateResponse =
  operations['postApiV1Documents']['responses'][201]['content']['application/json'];
export type CrmDocumentMergePatch = NonNullable<
  operations['patchApiV1DocumentsByDocumentRef']['requestBody']
>['content']['application/merge-patch+json'];

/** Generated canonical response for GET /api/v1/registrations. */
export type CrmRegistrationCollectionResponse =
  operations['getApiV1Registrations']['responses'][200]['content']['application/json'];
export type CrmRegistrationSelectorItem =
  CrmRegistrationCollectionResponse['data']['items'][number];

/** Runtime boundary for the approved synchronous Document write response. */
const documentMirrorOutcomeSchema = z.object({
  state: z.enum(['confirmed', 'failed', 'unknown', 'dependency_pending']),
  code: z
    .enum([
      'PROVIDER_REJECTED',
      'OUTCOME_UNKNOWN',
      'IDENTITY_PENDING',
      'PREDECESSOR_PENDING',
      'DELIVERY_UNAVAILABLE',
    ])
    .nullable(),
});
export const crmDocumentDirectMutationResponseSchema: z.ZodType<CrmDocumentCreateResponse> =
  z.object({
    data: z.object({ id: z.uuid() }),
    meta: z.object({
      localCommitted: z.literal(true),
      operationId: z.uuid(),
      identityBound: z.boolean().nullable(),
      mirror: documentMirrorOutcomeSchema.extend({
        effects: z.array(documentMirrorOutcomeSchema.extend({ effect: z.string().min(1) })),
      }),
    }),
  });
export type CrmDocumentDirectMutationResponse = z.infer<
  typeof crmDocumentDirectMutationResponseSchema
>;

export type CrmDocumentLifecycleResponse =
  operations['postApiV1DocumentsByDocumentIdCancelSubmission']['responses'][200]['content']['application/json'];

export const crmDocumentLifecycleResponseSchema: z.ZodType<CrmDocumentLifecycleResponse> =
  z.strictObject({
    data: z.strictObject({ documentId: z.uuid(), accepted: z.literal(true) }),
  });
